"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { getReportColumnsQueryKey, queryConversations, reportColumns } from "@/lib/api/generated";
import { ApiError } from "@/lib/api/error";
import type { FilterIn, QueryIn, QueryOut, SortIn } from "@/lib/api/model";

import { CONVERSATIONS_QUERY_PREFIX } from "../lib/query-keys";
import { useConversationsStore } from "../store/conversations-store";
import { useConversationColumns } from "./use-conversation-columns";

export type QueryScope =
  | { project_id: string }
  | { job_id: string }
  | { job_candidate_id: string }
  | { employer_id: string };

export interface QueryOptions {
  filters: FilterIn[];
  attention?: string;
  sort?: SortIn;
  page: number;
  per_page: number;
}

function scopeToQueryIn(scope: QueryScope): QueryIn {
  if ("project_id" in scope) return { project_id: scope.project_id };
  if ("job_id" in scope) return { job_id: scope.job_id };
  if ("job_candidate_id" in scope) return { job_candidate_id: scope.job_candidate_id };
  return { employer_id: scope.employer_id };
}

function isColumnsStale(e: unknown): e is ApiError {
  return e instanceof ApiError && e.code === "COLUMNS_STALE";
}

export function useConversationsQuery(scope: QueryScope, opts: QueryOptions) {
  const qc = useQueryClient();
  const columns = useConversationColumns();
  const hydrateRows = useConversationsStore((s) => s.hydrateRows);
  const body: QueryIn = {
    ...scopeToQueryIn(scope),
    filters: opts.filters,
    attention: opts.attention ?? null,
    sort: opts.sort ?? null,
    page: opts.page,
    per_page: opts.per_page,
    columns_version: columns.data?.version ?? null,
  };
  // `columns_version` stays in the POST body but not the key: putting it in
  // the key would start a second query after COLUMNS_STALE updates the
  // columns cache, so the inner retry would no longer be "exactly once".
  const { columns_version: _cv, ...keyBody } = body;
  void _cv;
  const q = useQuery<QueryOut>({
    queryKey: [...CONVERSATIONS_QUERY_PREFIX, keyBody],
    enabled: columns.data !== undefined,
    queryFn: async () => {
      try {
        return await queryConversations(body);
      } catch (e) {
        if (isColumnsStale(e)) {
          await qc.invalidateQueries({ queryKey: getReportColumnsQueryKey() });
          const fresh = await qc.fetchQuery({
            queryKey: getReportColumnsQueryKey(),
            queryFn: () => reportColumns(),
          });
          return await queryConversations({
            ...body,
            columns_version: fresh.version,
          });
        }
        throw e;
      }
    },
  });
  useEffect(() => {
    if (q.data) hydrateRows(q.data.rows);
  }, [q.data, hydrateRows]);
  return {
    ...q,
    error: q.error ?? columns.error,
    isLoading: q.isLoading || columns.isLoading,
  };
}
